import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { Button, Card, Checkbox, Input, Spinner } from '@/components/ui';

/** AUTH-1, AUTH-4, AUTH-7. */
export function LoginPage() {
  const { t } = useTranslation();
  const { login, status, sessionExpired, dismissSessionExpired } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const next = searchParams.get('next') ?? '/';

  if (status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center">
        <Spinner className="h-6 w-6 text-muted" />
      </div>
    );
  }

  // AUTH-4: an authenticated user has no business on the login screen.
  if (status === 'authenticated') return <Navigate to={next} replace />;

  const onSubmit = async (event: FormEvent) => {
    // Without this the browser fires its own GET and races the fetch -- exactly
    // the bug AddEditRequest.handleSubmit had.
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    dismissSessionExpired();

    try {
      await login(phone.trim(), password, rememberMe);
      navigate(next, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : t('errors.generic'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-bg p-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-5 flex flex-col items-center gap-2 text-center">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand text-sm font-bold text-brand-fg">
            S
          </div>
          <h1 className="text-lg font-semibold">{t('app.name')}</h1>
          <p className="text-sm text-muted">{t('auth.welcome')}</p>
        </div>

        {/* AUTH-6: an expired session says so, instead of a generic toast. */}
        {sessionExpired && (
          <div
            role="status"
            className="mb-4 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn"
          >
            {t('auth.sessionExpired')}
          </div>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
          <div className="flex flex-col gap-1">
            <label htmlFor="phone" className="text-xs font-medium">
              {t('auth.phone')}
            </label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="username"
              required
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              aria-invalid={Boolean(error)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-xs font-medium">
              {t('auth.password')}
            </label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={Boolean(error)}
            />
          </div>

          {/* AUTH-7: bound, and it genuinely changes the session lifetime. */}
          <Checkbox
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            label={t('auth.rememberMe')}
          />

          {error && (
            <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" loading={submitting} className="mt-1 w-full">
            {submitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * AUTH-3: the guard that did not exist. Every route except /login sits behind
 * it, and an anonymous visitor is sent to login with a `next` pointing back at
 * wherever they were trying to go.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center">
        <Spinner className="h-6 w-6 text-muted" />
      </div>
    );
  }

  if (status === 'anonymous') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  return <>{children}</>;
}
